import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type EmailKind,
  type EmailPreferences,
  fetchEmailHistory,
  fetchEmailPreferences,
  setEmailPreference,
} from "../api/index.js";
import { queryKeys } from "../query-keys.js";

export function useEmailPreferences(workspaceId: string, reportId: string) {
  return useQuery({
    queryKey: queryKeys.emailPreferences(workspaceId, reportId),
    queryFn: () => fetchEmailPreferences(workspaceId, reportId),
  });
}

/**
 * Flips one switch at once, and puts it back if the server refuses.
 *
 * Optimistic because a checkbox that waits a round trip to tick reads as
 * broken; the rollback is what keeps it from showing a setting that was never
 * saved (AGENTS.md §15a).
 */
export function useSetEmailPreference(workspaceId: string, reportId: string) {
  const client = useQueryClient();
  const key = queryKeys.emailPreferences(workspaceId, reportId);
  return useMutation({
    mutationFn: (change: { readonly kind: EmailKind; readonly enabled: boolean }) =>
      setEmailPreference(workspaceId, reportId, change),
    onMutate: async (change) => {
      await client.cancelQueries({ queryKey: key });
      const before = client.getQueryData<EmailPreferences>(key);
      if (before !== undefined) {
        client.setQueryData<EmailPreferences>(key, {
          ...before,
          kinds: before.kinds.map((entry) =>
            entry.kind === change.kind ? { ...entry, enabled: change.enabled } : entry,
          ),
        });
      }
      return { before };
    },
    onError: (_error, _change, context) => {
      if (context?.before !== undefined) client.setQueryData(key, context.before);
    },
    onSuccess: (saved) => {
      client.setQueryData(key, saved);
    },
  });
}

export function useEmailHistory(workspaceId: string, reportId: string) {
  return useQuery({
    queryKey: queryKeys.emailHistory(workspaceId, reportId),
    queryFn: () => fetchEmailHistory(workspaceId, reportId),
  });
}
